
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { axiosInstance } from '@/modules/shared/lib/axios'

import { ContractDetailsModel } from '../types/contracts'
import { useInvalidateDriverRequiredActions } from '@/lib/api/company/drivers/useQueryDriverRequiredActions'

const useContractService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  // Umowa zmienia zadania przy kierowcy (brak umowy, ulga dla młodych) - bez tego znacznik
  // w panelu wisi do końca staleTime mimo poprawionej umowy.
  const invalidateDriverRequiredActions = useInvalidateDriverRequiredActions()
  const getContracts = async ({
    companyId,
    driverId,
  }: {
    companyId: string
    driverId: number
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/contract?driverId=${driverId}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getContractTemplates = async ({ companyId }: { companyId: string }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/contract/contractTemplates`,
        {},
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const getContractDetails = async ({
    companyId,
    contractId,
    fromTemplate,
  }: {
    companyId: string
    contractId: string
    fromTemplate: boolean
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/contract/contractDetails?id=${contractId}&fromTemplate=${fromTemplate}`,
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const calculateOverdueLeave = async ({
    companyId,
    driverId,
    year,
  }: {
    companyId: string
    driverId: number
    year: number
  }): Promise<number | null> => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.get(
        `/api/${companyId}/contract/calculateOverdueLeave?driverId=${driverId}&year=${year}`,
      )
      return res.data?.overdueLeave ?? null
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const getGroupedContracts = async ({
    companyId,
    driversIds,
  }: {
    companyId: string
    driversIds: string[]
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `api/${companyId}/contract/groupedContractsInfo`,
        { drivers: driversIds },
      )
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }
  const deletecontractTemplate = async ({
    companyId,
    templateId,
  }: {
    companyId: string
    templateId: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(
        `/api/${companyId}/contract/deleteTemplate`,
        {
          companyId,
          templateId,
        },
      )
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const deleteContracts = async ({
    companyId,
    driverId,
    contractsIds,
  }: {
    companyId: string
    driverId: number
    contractsIds: number[]
  }) => {
    const stopLoading = startLoading()
    try {
      const results = await Promise.all(
        contractsIds.map((id) =>
          axiosInstance.post(`/api/${companyId}/contract/delete`, {
            id,
            driverId,
          }),
        ),
      )
      invalidateDriverRequiredActions()
      return results.map((res) => res.data)
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const createContract = async ({
    companyId,
    driverId,
    contractDetails,
  }: {
    companyId: string
    driverId: number
    contractDetails: ContractDetailsModel
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`api/${companyId}/contract/save`, {
        companyId,
        driverId,
        contractDetails,
      })
      invalidateDriverRequiredActions()
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const createGroupedContract = async ({
    companyId,
    driversIds,
    contractDetails,
  }: {
    companyId: string
    driversIds: number[]
    contractDetails: ContractDetailsModel
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`api/${companyId}/contract/saveGrouped`, {
        companyId,
        driversIds,
        contractDetails,
      })
      invalidateDriverRequiredActions()
      return res.data
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const createContractTemplate = async ({
    companyId,
    driverId,
    contractId,
    templateName,
  }: {
    companyId: string
    driverId: number
    contractId: string
    templateName: string
  }) => {
    const stopLoading = startLoading()
    try {
      const res = await axiosInstance.post(`/api/${companyId}/contract/createContractTemplate`, {
        companyId,
        driverId,
        contractId,
        templateName,
      })
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  return {
    getContracts,
    getContractDetails,
    getContractTemplates,
    getGroupedContracts,
    calculateOverdueLeave,

    createContractTemplate,
    createContract,
    createGroupedContract,

    deletecontractTemplate,
    deleteContracts,
  }
}

export default useContractService
